// Pure helpers for the doctor's My Patients page: Singapore calendar-month
// counts and grouping by referring dentist or clinic. They only ever see the
// doctor's own session-scoped case list.

export interface CaseDates {
  referral_received_date: string | null;
  created_at: string;
  completed_at: string | null;
  status_changed_at: string | null;
}

const SG_MONTH = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Singapore',
  year: 'numeric',
  month: '2-digit',
});

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** "YYYY-MM" in Singapore time, or null for a missing or unreadable value. */
export const singaporeMonthKey = (value: string | Date | null | undefined): string | null => {
  if (!value) return null;
  // A bare date is already a clinic calendar date; parsing it as UTC midnight
  // would be right here too, but slicing avoids any timezone question.
  if (typeof value === 'string' && DATE_ONLY.test(value)) return value.slice(0, 7);
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const parts = SG_MONTH.formatToParts(date);
  const year = parts.find((part) => part.type === 'year')?.value;
  const month = parts.find((part) => part.type === 'month')?.value;
  return year && month ? `${year}-${month}` : null;
};

const receivedMonth = (record: CaseDates) =>
  singaporeMonthKey(record.referral_received_date) ?? singaporeMonthKey(record.created_at);

/** Referred to the doctor during the given month. */
export const isNewInMonth = (record: CaseDates, month: string): boolean =>
  receivedMonth(record) === month;

/**
 * The case was open at some point during the given month: received on or
 * before it, and either still open or closed during or after it.
 */
export const isActiveInMonth = (record: CaseDates, isClosed: boolean, month: string): boolean => {
  const received = receivedMonth(record);
  if (!received || received > month) return false;
  if (!isClosed) return true;
  const closed =
    singaporeMonthKey(record.completed_at) ?? singaporeMonthKey(record.status_changed_at);
  return closed !== null && closed >= month;
};

export interface ReferrerGroup<T> {
  key: string;
  label: string;
  /** Other names seen for the same dentist or clinic, e.g. after a rename. */
  otherLabels: string[];
  clinics: string[];
  items: T[];
}

export const UNKNOWN_REFERRER_KEY = 'unknown';

const normalise = (value: string | null | undefined) =>
  (value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();

/** The group key a single case belongs to. */
export const referrerKey = (id: string | null | undefined, name: string | null | undefined) =>
  id ? `id:${id}` : normalise(name) ? `name:${normalise(name)}` : UNKNOWN_REFERRER_KEY;

interface ReferrerFields {
  id: string | null | undefined;
  name: string | null | undefined;
  clinic?: string | null | undefined;
}

/**
 * Group by the stable id first, then by the normalised name, so a renamed
 * dentist stays one group. Cases with neither land in one Unknown group,
 * listed last; the rest are ordered by size, then name.
 */
export const groupByReferrer = <T>(
  items: T[],
  fields: (item: T) => ReferrerFields,
  unknownLabel: string,
): ReferrerGroup<T>[] => {
  const groups = new Map<string, ReferrerGroup<T>>();
  for (const item of items) {
    const { id, name, clinic } = fields(item);
    const cleanName = (name ?? '').trim().replace(/\s+/g, ' ');
    const key = referrerKey(id, name);
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        label: key === UNKNOWN_REFERRER_KEY ? unknownLabel : cleanName,
        otherLabels: [],
        clinics: [],
        items: [],
      };
      groups.set(key, group);
    }
    group.items.push(item);
    const seen = [group.label, ...group.otherLabels].map(normalise);
    if (cleanName && !seen.includes(normalise(cleanName))) {
      group.otherLabels.push(cleanName);
    }
    const cleanClinic = (clinic ?? '').trim();
    if (cleanClinic && !group.clinics.includes(cleanClinic)) group.clinics.push(cleanClinic);
  }
  return [...groups.values()].sort((a, b) => {
    if (a.key === UNKNOWN_REFERRER_KEY) return 1;
    if (b.key === UNKNOWN_REFERRER_KEY) return -1;
    return b.items.length - a.items.length || a.label.localeCompare(b.label);
  });
};
